import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User } from '../types'
import toast from 'react-hot-toast'

const VM_WAKE_SERVICE_URL = (import.meta.env.VITE_WAKE_SERVICE_URL as string | undefined)?.trim() || ''
const VM_WAKE_SERVICE_TOKEN = (import.meta.env.VITE_WAKE_SERVICE_TOKEN as string | undefined)?.trim() || ''
const VM_WAKE_POLL_INTERVAL_MS = Number(import.meta.env.VITE_WAKE_POLL_INTERVAL_MS || 3000)
const VM_WAKE_MAX_WAIT_MS = Number(import.meta.env.VITE_WAKE_MAX_WAIT_MS || 180000)
const VM_HEALTH_TIMEOUT_MS = Number(import.meta.env.VITE_VM_HEALTH_TIMEOUT_MS || 4000)

// --- Funções Auxiliares ---

function trimRightSlash(input: string): string {
  return input.replace(/\/+$/, '')
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await fetch(url, { ...options, signal: controller.signal })
  } finally {
    clearTimeout(timeout)
  }
}

async function checkApiHealth(apiBaseUrl: string): Promise<boolean> {
  const base = trimRightSlash(apiBaseUrl)
  const candidates: string[] = []

  // Quando a base já vem com /api, priorizamos /api/health para evitar
  // problemas de CORS no /health raiz servido pelo Nginx.
  if (base.endsWith('/api')) {
    const origin = base.slice(0, -4)
    candidates.push(`${base}/health`)
    candidates.push(`${origin}/health`)
  } else {
    candidates.push(`${base}/api/health`)
    candidates.push(`${base}/health`)
  }

  for (const url of candidates) {
    try {
      const res = await fetchWithTimeout(url, { method: 'GET' }, VM_HEALTH_TIMEOUT_MS)
      if (res.ok) return true
    } catch {
      // tenta próximo endpoint candidato
    }
  }

  return false
}

async function requestVmWake(storeName?: string): Promise<void> {
  if (!VM_WAKE_SERVICE_URL) {
    throw new Error(
      'Serviço de wake-up não configurado. Defina VITE_WAKE_SERVICE_URL para permitir ligar a VM automaticamente.'
    )
  }

  // A Edge Function do Supabase escuta direto na URL (sem /wake no final)
  const endpoint = trimRightSlash(VM_WAKE_SERVICE_URL)
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }

  // Preferir token dedicado para wake-up (não depende do JWT do usuário)
  // Mantemos fallback para token do usuário por compatibilidade.
  if (VM_WAKE_SERVICE_TOKEN) {
    headers['x-wake-token'] = VM_WAKE_SERVICE_TOKEN
    headers.Authorization = `Bearer ${VM_WAKE_SERVICE_TOKEN}`
  } else {
    const supabaseToken = getPersistedSupabaseToken()
    if (supabaseToken) {
      headers.Authorization = `Bearer ${supabaseToken}`
    }
  }

  // Supabase Edge Functions requerem o apikey header
  const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim()
  if (anonKey) {
    headers.apikey = anonKey
  }

  const res = await fetchWithTimeout(
    endpoint,
    {
      method: 'POST',
      headers,
      body: JSON.stringify({
        storeName: storeName || null,
        source: 'frontend-login',
        requestedAt: new Date().toISOString(),
      }),
    },
    12000
  )

  if (!res.ok && res.status !== 202) {
    let detail = ''
    try {
      const payload = await res.json()
      detail = payload?.error ? ` (${payload.error})` : ''
    } catch {
      // ignore parse errors
    }
    throw new Error(`Falha ao solicitar wake-up da VM${detail}`)
  }
}

function getPersistedSupabaseToken(): string | null {
  try {
    const raw = localStorage.getItem('auth-storage')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed?.state?.token || null
  } catch {
    return null
  }
}

// Wrapper de fetch com retentativas para robustez da rede
async function fetchWithRetry(
  url: string,
  options: RequestInit,
  retries = 3,
  delay = 1000
): Promise<Response> {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await fetch(url, options)
      if (!response.ok && response.status >= 500) {
        // Tentar novamente em erros de servidor
        throw new Error(`Server error: ${response.status}`)
      }
      return response
    } catch (error) {
      if (i === retries - 1) throw error
      await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)))
    }
  }
  throw new Error('Fetch failed after multiple retries')
}

function resolveOtpApiUrl(_email: string): string {
  if (import.meta.env.DEV) {
    return '/api'
  }
  // Em produção, VM compartilhada
  return `https://api-editor.wicomm.com.br/api`
}

// Chamada de backend para enviar OTP
async function sendOtpToBackend(email: string) {
  const apiUrl = resolveOtpApiUrl(email)
  const response = await fetchWithRetry(`${apiUrl}/otp/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Erro ao enviar OTP')
  return data
}

// Chamada de backend para verificar OTP
async function verifyOtpWithBackend(email: string, token: string) {
  const apiUrl = resolveOtpApiUrl(email)
  const response = await fetchWithRetry(`${apiUrl}/otp/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, otp: token }),
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Erro ao verificar OTP')
  // Normalizar o formato para { user, token }
  return {
    user: data.user,
    token: data.access_token ?? data.token,
  }
}


// --- Store Zustand ---

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isWakingUp: boolean
  wakeUpMessage: string | null
  requiresStoreSelection: boolean // Novo estado
  useDevBranch: boolean // Flag para usar branch site-editor
  sendOTP: (email: string) => Promise<void>
  verifyOTP: (email: string, token: string, storeName?: string) => Promise<void>
  emergencyLogin: (email: string, password: string, storeName?: string) => Promise<void>
  selectStore: (storeName: string) => Promise<void>
  logout: () => void
  getStoreNameByEmail: (email: string) => Promise<{ storeName: string; apiUrl: string }>
  validateStore: (storeName: string) => Promise<{ exists: boolean; apiBaseUrl: string; previewBaseUrl: string }>
  ensureVmReady: (apiBaseUrl: string, storeName?: string) => Promise<void>
  clearWakeUpState: () => void
  setUseDevBranch: (value: boolean) => void
}

const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      isWakingUp: false,
      wakeUpMessage: null,
      requiresStoreSelection: false, // Estado inicial
      useDevBranch: false, // Flag para usar branch site-editor

      setUseDevBranch: (value: boolean) => {
        set({ useDevBranch: value })
      },

      clearWakeUpState: () => {
        set({ isWakingUp: false, wakeUpMessage: null })
      },

      ensureVmReady: async (apiBaseUrl: string, storeName?: string) => {
        if (import.meta.env.DEV) return

        const healthy = await checkApiHealth(apiBaseUrl)
        if (healthy) {
          get().clearWakeUpState()
          return
        }

        set({
          isWakingUp: true,
          wakeUpMessage: 'Seu ambiente está inativo. Estamos ligando a VM, isso pode levar alguns minutos...'
        })

        await requestVmWake(storeName)

        const startedAt = Date.now()
        while (Date.now() - startedAt < VM_WAKE_MAX_WAIT_MS) {
          const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000)
          set({
            isWakingUp: true,
            wakeUpMessage: `Ligando ambiente da loja... (${elapsedSeconds}s). Aguarde, estamos preparando tudo para você.`
          })

          const currentHealthy = await checkApiHealth(apiBaseUrl)
          if (currentHealthy) {
            set({
              isWakingUp: false,
              wakeUpMessage: null
            })
            return
          }

          await new Promise((resolve) => setTimeout(resolve, VM_WAKE_POLL_INTERVAL_MS))
        }

        set({
          isWakingUp: false,
          wakeUpMessage: null
        })
        throw new Error('Seu ambiente ainda está iniciando. Tente novamente em alguns instantes.')
      },

      sendOTP: async (email) => {
        set({ isLoading: true, requiresStoreSelection: false }) // Reseta no início
        try {
          await get().ensureVmReady(resolveOtpApiUrl(email))
          // Apenas dispara o OTP via backend
          await sendOtpToBackend(email)
        } catch (error: any) {
          console.error('❌ Erro ao enviar OTP:', error)
          toast.error(error.message || 'Falha ao enviar código de acesso.')
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      verifyOTP: async (email, token, storeName) => {
        set({ isLoading: true })
        try {
          await get().ensureVmReady(resolveOtpApiUrl(email), storeName)
          // 1. Verifica o OTP com o backend
          const { user: backendUser, token: sessionToken } = await verifyOtpWithBackend(email, token)

          // 2. Verifica se o usuário é multistore após validar o código
          const resolved = await get().getStoreNameByEmail(email)
          
          if (resolved.storeName === 'multistore') {
            console.log('🔧 Usuário multistore detectado. Exigindo seleção de loja.')
            // Fazer login parcial - usuário autenticado mas precisa selecionar store
            const partialUser = { ...backendUser, storeName: 'multistore', email, isMultiStore: true }
            
            // Renova o timer de inatividade
            localStorage.setItem('auth_last_active', Date.now().toString())
            
            set({ 
              user: partialUser,
              token: sessionToken,
              isAuthenticated: true,
              requiresStoreSelection: true, 
              isLoading: false 
            })
            return // Parar aqui para permitir seleção de store
          }

          // 3. Para usuários normais (não-multistore)
          let finalStoreName = resolved.storeName
          let apiUrl = resolved.apiUrl

          // 4. Define o estado final de autenticação
          const finalUser = { ...backendUser, storeName: finalStoreName, email }
          
          // Renova o timer de inatividade
          localStorage.setItem('auth_last_active', Date.now().toString())
          
          set({
            user: finalUser,
            token: sessionToken,
            isAuthenticated: true,
            requiresStoreSelection: false, // Reseta o estado
          })

          // 5. Buscar URLs corretas da loja antes de configurar o localStorage
          let previewBaseUrl = '' // será preenchido pelo /render/load (storefrontPort) em dev

          const isProduction = window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1')
          console.log(`🔍 Ambiente detectado: ${isProduction ? 'PRODUÇÃO' : 'DESENVOLVIMENTO'}`)

          if (isProduction) {
            try {
              console.log(`🔍 Buscando URLs para a loja '${finalStoreName}'...`)
              const validation = await get().validateStore(finalStoreName)
              console.log(`✅ URLs encontradas:`, validation)
              apiUrl = validation.apiBaseUrl
              previewBaseUrl = validation.previewBaseUrl
              console.log(`✅ apiBaseUrl definida como: ${apiUrl}`)
              console.log(`✅ previewBaseUrl definido como: ${previewBaseUrl}`)
            } catch (error) {
              console.error('❌ Erro ao buscar URLs da loja:', error)
              toast.error('Não foi possível carregar a configuração da loja.')
              set({ isLoading: false })
              return;
            }
          }

          const siteConfig = {
            storeName: finalStoreName,
            apiBaseUrl: import.meta.env.DEV ? '/api' : apiUrl,
            previewBaseUrl,
          }
          localStorage.setItem('site-config', JSON.stringify(siteConfig))
          console.log(`✅ Login bem-sucedido para a loja '${finalStoreName}'. Configuração salva.`, siteConfig)

          if (!import.meta.env.DEV) {
            await get().ensureVmReady(siteConfig.apiBaseUrl, finalStoreName)
          }

          // Chamar setup do repositório SEMPRE (modo normal ou dev), exceto para perfil "multistore"
          const { useDevBranch: devBranchForSetup } = get()
          console.log(`🔍 Verificando setup de repositório para ${finalStoreName}: useDevBranch = ${devBranchForSetup}`)
          if (finalStoreName.toLowerCase() !== 'multistore') {
            try {
              console.log(`🔧 Chamando setup do repositório para ${finalStoreName} (dev mode = ${devBranchForSetup})...`)
              const { authApi } = await import('../services/api')
              await authApi.setup(devBranchForSetup)
              console.log(`✅ Setup do repositório concluído para ${finalStoreName} (dev mode = ${devBranchForSetup})`)
            } catch (setupError: any) {
              console.error('❌ Erro ao fazer setup do repositório:', setupError)
              // Não falhar o login se o setup falhar, mas logar o erro
              if (setupError?.response?.data?.code === 'BRANCH_NOT_FOUND') {
                toast.error(`Branch 'site-editor' não encontrada para ${finalStoreName}`)
              } else {
                toast.error(`Erro ao configurar repositório: ${setupError?.response?.data?.error || setupError?.message}`)
              }
            }
          } else {
            console.log(`ℹ️ StoreName é "multistore", pulando setup (é apenas um perfil, não uma loja real)`)
          }

        } catch (error: any) {
          console.error('❌ Erro ao verificar OTP:', error)
          toast.error(error.message || 'Código de acesso inválido ou expirado.')
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      emergencyLogin: async (email, password, storeName) => {
        set({ isLoading: true, requiresStoreSelection: false })
        try {
          await get().ensureVmReady(resolveOtpApiUrl(email), storeName)
          // 1. Fazer login de emergência com o backend
          const apiUrl = resolveOtpApiUrl(email)
          const response = await fetchWithRetry(`${apiUrl}/otp/emergency`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, storeName }),
          })
          
          const data = await response.json()
          if (!response.ok) throw new Error(data.error || 'Erro no login de emergência')
          
          const { user: backendUser, access_token: sessionToken } = data

          // 2. Verificar se o usuário é multistore
          const resolved = await get().getStoreNameByEmail(email)
          
          if (resolved.storeName === 'multistore') {
            // Usuário multistore - precisa selecionar store
            
            // Renova o timer de inatividade
            localStorage.setItem('auth_last_active', Date.now().toString())
            
            set({ 
              user: { ...backendUser, isMultiStore: true }, 
              token: sessionToken,
              isAuthenticated: true,
              requiresStoreSelection: true,
              isLoading: false 
            })
            return
          }

          // 3. Usuário single-store - configurar automaticamente
          const finalStoreName = storeName || resolved.storeName
          const siteConfig = {
            storeName: finalStoreName,
            apiBaseUrl: import.meta.env.DEV ? '/api' : resolved.apiUrl,
            previewBaseUrl: import.meta.env.DEV ? '' : resolved.apiUrl.replace('/api', '/preview'),
          }
          
          localStorage.setItem('site-config', JSON.stringify(siteConfig))
          
          const finalUser = { ...backendUser, storeName: finalStoreName, isMultiStore: false }
          
          // Renova o timer de inatividade
          localStorage.setItem('auth_last_active', Date.now().toString())
          
          set({ 
            user: finalUser, 
            token: sessionToken,
            isAuthenticated: true,
            requiresStoreSelection: false,
            isLoading: false 
          })
          
          console.log(`✅ Login de emergência bem-sucedido para a loja '${finalStoreName}'. Configuração salva.`, siteConfig)

        } catch (error: any) {
          console.error('❌ Erro no login de emergência:', error)
          toast.error(error.message || 'Email ou senha inválidos.')
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      selectStore: async (storeName) => {
        set({ isLoading: true })
        try {
          const { user, useDevBranch } = get()
          if (!user || !get().isAuthenticated) {
            throw new Error('Usuário não autenticado')
          }

          // Validar a loja selecionada
          const validation = await get().validateStore(storeName)
          if (!validation.exists) {
            throw new Error(`Loja '${storeName}' não encontrada ou inválida.`)
          }

          // Configurar para a loja selecionada
          if (import.meta.env.DEV) {
            const siteConfigFromValidation = {
              storeName: storeName,
              apiBaseUrl: '/api',
              previewBaseUrl: '',
            }
            localStorage.setItem('site-config', JSON.stringify(siteConfigFromValidation))
          } else {
            const siteConfigFromValidation = {
              storeName: storeName,
              apiBaseUrl: validation.apiBaseUrl,
              previewBaseUrl: validation.previewBaseUrl,
            }
            localStorage.setItem('site-config', JSON.stringify(siteConfigFromValidation))

            await get().ensureVmReady(validation.apiBaseUrl, storeName)
          }

          // Chamar setup do repositório SEMPRE (modo normal ou dev), exceto para perfil "multistore"
          console.log(`🔍 Verificando setup de repositório para ${storeName}: useDevBranch = ${useDevBranch}`)
          if (storeName.toLowerCase() !== 'multistore') {
            try {
              console.log(`🔧 Chamando setup do repositório para ${storeName} (dev mode = ${useDevBranch})...`)
              const { authApi } = await import('../services/api')
              await authApi.setup(useDevBranch)
              console.log(`✅ Setup do repositório concluído para ${storeName} (dev mode = ${useDevBranch})`)
            } catch (setupError: any) {
              console.error('❌ Erro ao fazer setup do repositório:', setupError)
              // Não falhar o login se o setup falhar, mas logar o erro
              if (setupError?.response?.data?.code === 'BRANCH_NOT_FOUND') {
                toast.error(`Branch 'site-editor' não encontrada para ${storeName}`)
              } else {
                toast.error(`Erro ao configurar repositório: ${setupError?.response?.data?.error || setupError?.message}`)
              }
            }
          } else {
            console.log(`ℹ️ StoreName é "multistore", pulando setup (é apenas um perfil, não uma loja real)`)
          }

          // Finalizar login para usuário multistore com store selecionada
          const finalUser = { ...user, storeName: storeName, isMultiStore: true }
          set({ 
            user: finalUser, 
            requiresStoreSelection: false,
            isLoading: false 
          })
          
          console.log(`✅ Loja '${storeName}' selecionada com sucesso.`)

        } catch (error: any) {
          console.error('❌ Erro ao selecionar loja:', error)
          toast.error(error.message || 'Erro ao selecionar loja.')
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          requiresStoreSelection: false,
          useDevBranch: false,
        })
        localStorage.removeItem('site-config')
        localStorage.removeItem('auth_last_active')
        console.log('✅ Logout e limpeza concluídos')
      },

      getStoreNameByEmail: async (email) => {
        try {
          await get().ensureVmReady(resolveOtpApiUrl(email))
          // Usar VM wicomm como VM de autenticação até descobrir o storeName
          const authApiUrl = import.meta.env.DEV 
            ? '/api/resolve-store'
            : 'https://api-editor.wicomm.com.br/api/resolve-store'
          
          const response = await fetchWithRetry(authApiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          })
          const data = await response.json()
          if (!response.ok) {
            throw new Error(data.error || 'Erro ao buscar storeName')
          }
          if (!data.storeName) {
            throw new Error('StoreName não encontrado para este usuário.')
          }
          return { storeName: data.storeName, apiUrl: resolveOtpApiUrl(email) }
        } catch (error: any) {
          console.error(`❌ Erro na busca do storeName: ${error.message}`)
          throw error
        }
      },

      validateStore: async (storeName) => {
        try {
          await get().ensureVmReady(resolveOtpApiUrl(storeName), storeName)
          // Usar VM wicomm como VM de autenticação para validar lojas também
          const authApiUrl = import.meta.env.DEV 
            ? '/api/validate-store'
            : 'https://api-editor.wicomm.com.br/api/validate-store'
          
          const response = await fetchWithRetry(authApiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ storeName }),
          })
          const data = await response.json()
          if (!response.ok) {
            throw new Error(data.error || 'Erro ao validar a loja')
          }
          // Normalizar as chaves esperadas
          return {
            exists: Boolean(data.exists),
            apiBaseUrl: String(data.apiBaseUrl || ''),
            previewBaseUrl: String(data.previewBaseUrl || ''),
          }
        } catch (error: any) {
          console.error(`❌ Erro na validação da loja: ${error.message}`)
          throw error
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        requiresStoreSelection: state.requiresStoreSelection,
        useDevBranch: state.useDevBranch, // Persistir flag de dev mode
      }),
    }
  )
)

export { useAuthStore }
